import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';

import '../../styles/buttons.css';
import '../../styles/popup.css';

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Autocomplete from '@mui/material/Autocomplete';

const SignUpForm = ({ onNewManagerProperty }) => {
    const [properties, setProperties] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const [showPassword, setShowPassword] = React.useState(false);

    const [formData, setFormData] = useState({
        given_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        password: '',
        type: '',
        status: 'active',
        property: ''
    });

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event) => {
        event.preventDefault();
    };

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const rolesResponse = await axios.get(`${config.API_URL}/v2/role/propertyRole/`, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
                });

                const managerRoles = rolesResponse.data.filter((role) => role.role === 'manager' && role.property !== null);
                const properties = managerRoles.map((role) => role.property);

                console.log(properties)
                setProperties(properties);
            } catch (error) {
                console.error('Error fetching properties:', error);
                setError('Error fetching properties. Please try again.');
            }
        };

        fetchAccounts();
    }, []);

    const toggleOverlay = () => setOverlayOpen(!isOverlayOpen);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setError(null);
            setSuccess(null);
            const response = await axios.post(`${config.API_URL}/v2/auth/signup`, formData);

            if (response.status === 201) {
                const newUser = response.data;

                setSuccess('User successfully created!');
                toast.success('User successfully created!');
                setFormData({
                    given_name: '',
                    middle_name: '',
                    last_name: '',
                    email: '',
                    password: '',
                    type: '',
                    status: 'active',
                    property: '',
                });

                if (onNewManagerProperty) {
                    onNewManagerProperty(newUser);
                }
                toggleOverlay();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'An error occurred');
        }
    };

    return (
        <div>
            <button onClick={toggleOverlay} className="header-button">
                <i className="fas fa-add"></i>Account
            </button>
            {isOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={toggleOverlay}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl text-black">Sign Up</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="popup-columns">
                                <TextField
                                    label="Given Name"
                                    name="given_name"
                                    variant="outlined"
                                    fullWidth
                                    value={formData.given_name}
                                    onChange={handleInputChange}
                                    sx={{ marginBottom: '1rem' }}
                                    required
                                />
                                <TextField
                                    label="Middle Name"
                                    name="middle_name"
                                    variant="outlined"
                                    fullWidth
                                    value={formData.middle_name}
                                    onChange={handleInputChange}
                                    sx={{ marginBottom: '1rem' }}
                                />
                            </div>
                            <TextField
                                label="Last Name"
                                name="last_name"
                                variant="outlined"
                                fullWidth
                                value={formData.last_name}
                                onChange={handleInputChange}
                                sx={{ marginBottom: '1rem' }}
                                required
                            />
                            <TextField
                                label="Email"
                                name="email"
                                variant="outlined"
                                fullWidth
                                value={formData.email}
                                onChange={handleInputChange}
                                sx={{ marginBottom: '1rem' }}
                                required
                            />
                            <FormControl sx={{ width: '100%', marginBottom: '1rem' }} variant="outlined">
                                <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                                <OutlinedInput
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showPassword ? 'hide the password' : 'display the password'
                                                }
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                onMouseUp={handleMouseUpPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Password"
                                />
                            </FormControl>
                            <Autocomplete
                                name="type"
                                value={formData.type ? formData.type.charAt(0).toUpperCase() + formData.type.slice(1) : ''}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            type: newValue.toLowerCase(),
                                        }));
                                    }
                                }}
                                options={['owner', 'staff', 'manager', 'tenant']} // Add more roles as necessary
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        {option.charAt(0).toUpperCase() + option.slice(1)}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Account Type"
                                        variant="outlined"
                                        fullWidth
                                        required
                                    />
                                )}
                                sx={{ marginBottom: '1rem' }}
                            />
                            <Autocomplete
                                id="property"
                                name="property"
                                value={properties.find((prop) => prop._id === formData.property) || null}
                                onChange={(event, newValue) => {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        property: newValue ? newValue._id : '',
                                    }));
                                }}
                                options={properties}
                                getOptionLabel={(option) =>
                                    option.name
                                        ? option.name // If property has a name, display it
                                        : `${option?.property?.address?.streetNumber}, ${option?.property?.address?.street}, ${option?.property?.address?.city}, ${option?.property?.address?.state}, ${option?.property?.address?.country} - ${option?.property?.address?.postalCode}`
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Property"
                                        variant="outlined"
                                        fullWidth
                                        required
                                    />
                                )}
                                sx={{ marginBottom: '1rem' }}
                            />
                            <button type="submit" className="button">Sign Up</button>
                            {error && <div className="signup-error">{error}</div>}
                            {success && <div className="signup-success">{success}</div>}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignUpForm;
